<template>
  <div class="content-fluid">
    <main class="wrapper-main">
      <EnvironmentBanner />
      <BartopPlan />
      <DashboardHeaderComponent />
      <LoginBanner />
      <DashboardContentComponent />
      <NotificationDrawer />

      <!-- Modales de notificaciones (siempre disponibles) -->
      <ExpiredQuotesModal
        :visible.sync="showExpiredQuotesModal"
        @send-message="onBulkMessage($event, 'quotes')"
        @bulk-message="onBulkMessageDirect"
      />
      <DormantClientsModal
        :visible.sync="showDormantClientsModal"
        @send-message="onBulkMessage($event, 'dormant')"
      />
      <OverdueInvoicesModal
        :visible.sync="showOverdueInvoicesModal"
        @invoices-updated="$root.$emit('dashboard-kpis-refresh')"
        @send-reminder="onBulkMessageDirect"
      />
      <PendingCollectionModal
        :visible.sync="showPendingCollectionModal"
        @invoices-updated="$root.$emit('dashboard-kpis-refresh')"
        @send-reminder="onBulkMessageDirect"
      />

      <!-- Modal de envío masivo de mensajes -->
      <BulkMessageModal
        :visible.sync="showBulkMessageModal"
        :recipients="bulkMessageRecipients"
        :context="bulkMessageContext"
      />

      <!-- Preview de factura (se abre encima de cualquier modal) -->
      <InvoicePreview
        v-if="showInvoicePreview"
        :key="previewInvoice ? previewInvoice.id : null"
        :invoice="previewInvoice"
        :showModal="showInvoicePreview"
        :status="previewInvoice ? previewInvoice.status : ''"
        @close="closeInvoicePreview"
        @action="handlePreviewAction"
        @reminder-updated="refreshPreviewInvoice"
      />

      <!-- Modal de límite de plan -->
      <PlanLimitModal />

      <SigningConfirmModal
        v-if="previewInvoice"
        :visible.sync="showSigningModal"
        :documentNumber="previewInvoice ? previewInvoice.number_invoice : ''"
        :clientName="
          previewInvoice && previewInvoice.client
            ? previewInvoice.client.name
            : ''
        "
        :amount="previewInvoice ? previewInvoice.total : 0"
        @confirm="sendPreviewInvoiceSRI"
      />

      <!-- Modal de pago (se abre desde cualquier dropdown de factura) -->
      <PaymentModal
        v-if="paymentInvoice"
        :invoice="paymentInvoice"
        :open-tab="paymentModalTab"
        @close="
          paymentInvoice = null;
          paymentModalTab = null;
        "
        @payment-registered="onPaymentDone"
      />

      <!-- Preview de cotización (se abre encima de cualquier modal) -->
      <QuotePreview
        v-if="previewQuote"
        :quote="previewQuote"
        :idVersion="previewQuoteVersionId"
        :showModal="showQuotePreview"
        :status="previewQuote ? previewQuote.status : ''"
        @close="closeQuotePreview"
      />
    </main>
  </div>
</template>

<script>
import moment from "moment";
import DashboardHeaderComponent from "./DashboardHeaderComponent";
import DashboardContentComponent from "./DashboardContentComponent";
import BartopPlan from "@/modules/dashboard/plans/components/BartopPlan";
import NotificationDrawer from "@/components/NotificationDrawer";
import LoginBanner from "@/components/LoginBanner";
import EnvironmentBanner from "@/components/EnvironmentBanner";
import ExpiredQuotesModal from "@/modules/dashboard/reports/components/ExpiredQuotesModal";
import DormantClientsModal from "@/modules/dashboard/reports/components/DormantClientsModal";
import OverdueInvoicesModal from "@/modules/dashboard/reports/components/OverdueInvoicesModal";
import PendingCollectionModal from "@/modules/dashboard/reports/components/PendingCollectionModal";
import BulkMessageModal from "@/modules/dashboard/reports/components/BulkMessageModal";
import InvoicePreview from "@/modules/dashboard/sales/pages/invoices/components/InvoicePreview";
import QuotePreview from "@/modules/dashboard/sales/pages/quotes/components/QuotePreview";
import PaymentModal from "@/modules/dashboard/sales/pages/projects/components/PaymentModal";
import SigningConfirmModal from "@/components/General/SigningConfirmModal";
import PlanLimitModal from "@/modules/dashboard/plans/components/PlanLimitModal";
import invoiceService from "@/modules/dashboard/sales/pages/invoices/services/invoiceService";
import { signInvoiceSRI } from "@/modules/dashboard/sales/pages/invoices/services/signInvoiceSRI";
import quoteService from "@/modules/dashboard/sales/pages/quotes/services/quoteService";
import { signingEventBus } from "@/services/signingEventBus";

export default {
  name: "DashboardLayout",
  data: () => ({
    date: moment().format("MMMM Do YYYY, h:mm a"),
    showExpiredQuotesModal: false,
    showDormantClientsModal: false,
    showOverdueInvoicesModal: false,
    showPendingCollectionModal: false,
    showBulkMessageModal: false,
    bulkMessageRecipients: [],
    bulkMessageContext: "quotes",
    showInvoicePreview: false,
    previewInvoice: null,
    showSigningModal: false,
    verifyingInvoice: false,
    showQuotePreview: false,
    previewQuote: null,
    previewQuoteVersionId: null,
    paymentInvoice: null,
    paymentModalTab: null
  }),
  components: {
    DashboardHeaderComponent,
    DashboardContentComponent,
    BartopPlan,
    NotificationDrawer,
    LoginBanner,
    EnvironmentBanner,
    ExpiredQuotesModal,
    DormantClientsModal,
    OverdueInvoicesModal,
    PendingCollectionModal,
    BulkMessageModal,
    InvoicePreview,
    QuotePreview,
    PaymentModal,
    SigningConfirmModal,
    PlanLimitModal
  },
  mounted() {
    this.$store.dispatch("fetchUserPlan").then(this.checkDocumentLimitNotice);
    // Multi-usuario: carga rol/permisos del negocio seleccionado y procesa
    // invitaciones de equipo pendientes guardadas en localStorage.
    this.acceptPendingTeamInvite().finally(() => {
      this.$store.dispatch("loadMyPermissions");
    });
    this.$root.$on("notification-action", this.handleNotificationAction);
    this.$root.$on("preview-invoice", this.openInvoicePreview);
    this.$root.$on("preview-quote", this.openQuotePreview);
    this.$root.$on("open-bulk-message", this.onBulkMessageDirect);
    this.$root.$on("open-payment-modal", this.openPaymentModal);
  },
  beforeDestroy() {
    this.$root.$off("notification-action", this.handleNotificationAction);
    this.$root.$off("preview-invoice", this.openInvoicePreview);
    this.$root.$off("preview-quote", this.openQuotePreview);
    this.$root.$off("open-bulk-message", this.onBulkMessageDirect);
    this.$root.$off("open-payment-modal", this.openPaymentModal);
  },
  methods: {
    /**
     * Comunicado del límite mensual de documentos: al 80 % (aviso) y al 100 %
     * (límite alcanzado), una vez por mes y nivel. Es informativo: el cliente
     * puede seguir con su plan o mejorarlo.
     */
    checkDocumentLimitNotice() {
      const usage = this.$store.getters.documentUsage;
      if (!usage || !usage.limit || usage.percentage < 80) return;

      const level = usage.percentage >= 100 ? "limit" : "warning";
      const now = new Date();
      const planId = this.$store.getters.userPlan?.plan?.id || "plan";
      const key = `mydessk:doc-limit-notice:${planId}:${now.getFullYear()}-${now.getMonth() +
        1}:${level}`;
      try {
        if (localStorage.getItem(key)) return;
        localStorage.setItem(key, "1");
      } catch (e) {
        // Sin storage disponible: se muestra igual
      }

      this.$store.dispatch("triggerLimitModal", {
        feature: "documents_monthly_limit",
        usage,
        warning: level === "warning"
      });
    },
    /**
     * Si el usuario llegó desde un link de invitación (/invitacion?token=x)
     * y tuvo que loguearse/registrarse primero, el token quedó en
     * localStorage. Aquí se acepta automáticamente al entrar al dashboard.
     */
    async acceptPendingTeamInvite() {
      const token = localStorage.getItem("pending_team_invite");
      if (!token) return;
      try {
        const teamService = (
          await import("@/modules/dashboard/team/services/index")
        ).default;
        const { data } = await teamService.acceptInvitation({ token });
        if (data.success) {
          this.$notifications.success({
            message: data.message || "Te uniste al equipo."
          });
        }
      } catch (e) {
        const msg =
          e && e.response && e.response.data && e.response.data.message;
        if (msg) this.$notifications.error({ message: msg });
      } finally {
        localStorage.removeItem("pending_team_invite");
      }
    },
    handleNotificationAction(action) {
      switch (action) {
        case "expired-quotes":
          this.showExpiredQuotesModal = true;
          break;
        case "overdue-invoices":
          this.showOverdueInvoicesModal = true;
          break;
        case "dormant-clients":
          this.showDormantClientsModal = true;
          break;
        case "pending-collection":
          this.showPendingCollectionModal = true;
          break;
        case "approved-not-invoiced":
          this.$router.push("/dashboard/ventas/documentos").catch(() => {});
          break;
      }
    },
    onBulkMessage(payload, context) {
      this.bulkMessageRecipients = payload.recipients || [];
      this.bulkMessageContext = context || payload.context || "quotes";
      this.showBulkMessageModal = true;
    },
    onBulkMessageDirect(payload) {
      this.bulkMessageRecipients = payload.recipients || [];
      this.bulkMessageContext = payload.context || "invoices";
      this.showBulkMessageModal = true;
    },
    async openInvoicePreview(invoiceId) {
      // Abrir el preview INMEDIATAMENTE con placeholder, fetch en background.
      // El componente InvoicePreview muestra skeleton mientras invoice está vacío.
      this.previewInvoice = { id: invoiceId, _loading: true };
      this.showInvoicePreview = true;
      try {
        const response = await invoiceService.getInvoceId(invoiceId);
        if (response && response.data && response.data.data) {
          this.previewInvoice = response.data.data;
        } else {
          this.closeInvoicePreview();
          this.$message.error("No se pudo cargar la factura");
        }
      } catch (e) {
        this.closeInvoicePreview();
        this.$message.error("No se pudo cargar la factura");
      }
    },
    closeInvoicePreview() {
      this.showInvoicePreview = false;
      this.previewInvoice = null;
    },
    async refreshPreviewInvoice() {
      if (!this.previewInvoice) return;
      try {
        const response = await invoiceService.getInvoceId(
          this.previewInvoice.id
        );
        if (response && response.data && response.data.data) {
          const updated = response.data.data;
          this.previewInvoice = updated;
          // Notifica al card en la lista para que actualice el ícono de campana
          this.$root.$emit("invoice-reminder-updated", {
            id: updated.id,
            data: updated
          });
        }
      } catch (e) {
        // silencioso — el estado local en InvoicePreview ya refleja el cambio
      }
    },
    async verifyPreviewInvoiceSRI() {
      if (!this.previewInvoice) return;
      const invoiceId = this.previewInvoice.id;
      this.verifyingInvoice = true;
      try {
        const response = await invoiceService.verifyInvoiceSRI({
          user_business_invoice_id: invoiceId
        });
        const { success, message, data, no_key } = response.data;
        if (success) {
          this.previewInvoice = data;
          this.$message.success(
            "La factura está autorizada por el SRI. Estado actualizado a firmada."
          );
        } else if (no_key) {
          this.$message.warning(message);
        } else {
          this.$message.warning(
            message || "El SRI no confirmó la autorización."
          );
        }
      } catch (error) {
        this.$message.error(
          error?.response?.data?.message ||
            "No se pudo verificar el estado con el SRI."
        );
      } finally {
        this.verifyingInvoice = false;
      }
    },
    handlePreviewAction(action) {
      switch (action) {
        case "invoice-signature":
          this.showSigningModal = true;
          break;
        case "invoice-verify":
          this.verifyPreviewInvoiceSRI();
          break;
        case "invoice-view-doc":
          this.closeInvoicePreview();
          this.$router.push({
            name: "sales.invoices.view",
            params: { id: this.previewInvoice.id }
          });
          break;
        case "invoice-edit":
          this.closeInvoicePreview();
          this.$router.push({
            name: "sales.invoices.edit",
            params: { id: this.previewInvoice.id }
          });
          break;
        case "invoice-download":
          this.downloadPreviewInvoice();
          break;
      }
    },
    async sendPreviewInvoiceSRI() {
      if (!this.previewInvoice) return;
      const invoiceId = this.previewInvoice.id;
      const clientName = this.previewInvoice.client
        ? this.previewInvoice.client.name
        : "";
      const documentNumber = this.previewInvoice.number_invoice || "";
      this.showSigningModal = false;
      signingEventBus.$emit("signing:start", {
        id: invoiceId,
        clientName,
        documentNumber
      });
      try {
        const { success, message, data } = await signInvoiceSRI(invoiceId);
        if (success) {
          signingEventBus.$emit("signing:done", {
            id: invoiceId,
            clientName,
            documentNumber
          });
          this.previewInvoice = data;
        } else {
          signingEventBus.$emit("signing:error", {
            id: invoiceId,
            message: message || "Error al firmar"
          });
        }
      } catch (error) {
        signingEventBus.$emit("signing:error", {
          id: invoiceId,
          message:
            error?.response?.data?.message ||
            error?.message ||
            "Error al firmar"
        });
      }
    },
    async downloadPreviewInvoice() {
      if (!this.previewInvoice) return;
      try {
        const { id, number_invoice } = this.previewInvoice;
        const response = await invoiceService.invoiceDownload({ id });
        const { downloadFile } = await import("@/assets/utils/files");
        downloadFile(response.data, `Factura - #${number_invoice}`);
      } catch {
        // silent
      }
    },
    async openQuotePreview(quoteId) {
      // Abrir el preview INMEDIATAMENTE con placeholder, fetch en background.
      this.previewQuote = { id: quoteId, _loading: true, versions: [] };
      this.previewQuoteVersionId = null;
      this.showQuotePreview = true;
      try {
        const response = await quoteService.getQuoteId(quoteId);
        const data = response.data;
        const quote = data.quote || data.data;
        if (quote) {
          this.previewQuote = quote;
          this.previewQuoteVersionId =
            quote.versions && quote.versions.length
              ? quote.versions[0].id
              : null;
        } else {
          this.closeQuotePreview();
          this.$message.error("No se pudo cargar la cotización");
        }
      } catch (e) {
        this.closeQuotePreview();
        this.$message.error("No se pudo cargar la cotización");
      }
    },
    closeQuotePreview() {
      this.showQuotePreview = false;
      this.previewQuote = null;
      this.previewQuoteVersionId = null;
    },
    openPaymentModal(payload) {
      // Acepta una factura directa o { invoice, tab }.
      if (payload && payload.invoice) {
        this.paymentInvoice = payload.invoice;
        this.paymentModalTab = payload.tab || null;
      } else {
        this.paymentInvoice = payload;
        this.paymentModalTab = null;
      }
    },
    onPaymentDone(payload) {
      this.paymentInvoice = null;
      this.paymentModalTab = null;
      this.$root.$emit("dashboard-kpis-refresh");
      // Propagar para que cualquier listado de facturas actualice el card sin recargar
      if (payload && payload.invoiceId) {
        this.$root.$emit("invoice-payment-updated", {
          invoiceId: payload.invoiceId,
          summary:
            payload.result && payload.result.invoice_summary
              ? payload.result.invoice_summary
              : null
        });
      }
    }
  }
};
</script>

<style scoped lang="scss"></style>
